<template>
  <v-container>
    <v-layout row pt-5>
      <v-flex xs12 sm10 offset-sm1>
        <v-btn to="/iabf/admin/corps-pieces" small text>
          &lt; Back to Corps Pieces
        </v-btn>

        <h2 class="mt-2">{{ form.title || 'Edit Corps Piece' }}</h2>

        <!-- Delete button -->
        <v-dialog v-model="deleteDialog" width="500">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="red" dark small v-bind="attrs" v-on="on">
              Delete Piece
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="text-h5 grey lighten-2">Are you sure?</v-card-title>
            <v-card-text>
              <p>This will permanently delete "{{ form.title }}" and all its roles, videos, and assignments.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="deleteDialog = false">Cancel</v-btn>
              <v-btn color="red" text :loading="deleting" @click="onDelete">Yes, Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <hr class="my-4" />

        <!-- Basics section -->
        <h3>Piece Basics</h3>
        <v-text-field
          v-model="form.title"
          label="Title"
          outlined
          dense
          class="mt-2"
        />
        <v-textarea
          v-model="form.description"
          label="Description"
          outlined
          dense
          rows="3"
        />
        <v-text-field
          v-model="form.stageFormationUrl"
          label="Stage Formation URL"
          outlined
          dense
          hint="Link to a Google Doc, PDF, etc. Leave blank to hide this section for dancers."
          persistent-hint
        />
        <v-btn color="primary" :loading="savingBasics" @click="onSaveBasics">
          Save Basics
        </v-btn>
        <v-alert v-if="basicsSaved" type="success" dense text class="mt-2">Saved.</v-alert>

        <hr class="my-6" />

        <!-- Roles section -->
        <h3>Roles</h3>
        <v-expansion-panels v-if="form.roles.length > 0" accordion class="mt-3">
          <v-expansion-panel v-for="(role, roleIdx) in form.roles" :key="role.id">
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between" style="width:100%">
                <span>{{ role.name || '(unnamed role)' }}</span>
                <v-btn
                  icon
                  x-small
                  color="red"
                  class="mr-2"
                  @click.stop="onRemoveRole(roleIdx)"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>

              <!-- Role basics -->
              <v-text-field
                v-model="role.name"
                label="Role Name"
                outlined
                dense
                class="mt-2"
              />
              <v-text-field
                v-model="role.costumeColorField"
                label="Costume Color Field"
                outlined
                dense
                hint="e.g. assignedWaltzColor — the field name on the dancer doc"
                persistent-hint
              />
              <v-textarea
                v-model="role.costumeDescription"
                label="Costume Description (what IABF provides)"
                outlined
                dense
                rows="3"
              />
              <v-select
                v-model="role.shareVideosFromRole"
                :items="shareVideoOptions(role.id)"
                label="Share Videos From Role"
                outlined
                dense
                hint="If set, this role shows videos from the selected role instead of its own"
                persistent-hint
              />
              <v-btn
                color="primary"
                small
                :loading="savingRoleId === role.id"
                @click="onSaveRole(role)"
              >
                Save Role Details
              </v-btn>
              <v-alert v-if="savedRoleId === role.id" type="success" dense text class="mt-2">Saved.</v-alert>

              <v-divider class="my-4" />

              <!-- Videos sub-section -->
              <h4>Rehearsal Videos</h4>
              <div v-if="role.shareVideosFromRole">
                <p class="caption">
                  Videos shared from: <strong>{{ roleNameById(role.shareVideosFromRole) }}</strong>
                </p>
              </div>
              <div v-else>
                <div
                  v-for="(video, vidIdx) in sortedVideos(role.videos)"
                  :key="video.id"
                  class="video-row d-flex align-center mb-2"
                >
                  <div class="d-flex flex-column mr-2">
                    <v-btn icon x-small @click="onMoveVideoUp(role, vidIdx)">
                      <v-icon x-small>mdi-chevron-up</v-icon>
                    </v-btn>
                    <v-btn icon x-small @click="onMoveVideoDown(role, vidIdx)">
                      <v-icon x-small>mdi-chevron-down</v-icon>
                    </v-btn>
                  </div>
                  <div class="flex-grow-1">
                    <strong>{{ video.title }}</strong>
                    <span class="caption ml-2 grey--text">{{ (video.embedCode || '').substring(0, 60) }}…</span>
                  </div>
                  <v-btn icon x-small color="red" @click="onRemoveVideo(role, vidIdx)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <!-- Add video form -->
                <v-expand-transition>
                  <div v-if="showAddVideoForRole === role.id" class="add-video-form mt-2">
                    <v-text-field
                      v-model="newVideo.title"
                      label="Video Title"
                      outlined
                      dense
                    />
                    <v-textarea
                      v-model="newVideo.embedCode"
                      label="Embed Code (JW Player <iframe>)"
                      outlined
                      dense
                      rows="3"
                    />
                    <v-btn small color="primary" @click="onAddVideo(role)">Add Video</v-btn>
                    <v-btn small text @click="showAddVideoForRole = null">Cancel</v-btn>
                  </div>
                </v-expand-transition>
                <v-btn
                  v-if="showAddVideoForRole !== role.id"
                  small
                  outlined
                  class="mt-2"
                  @click="onOpenAddVideo(role.id)"
                >
                  + Add Video
                </v-btn>
              </div>

              <v-divider class="my-4" />

              <!-- Dancer assignment sub-section -->
              <h4>Dancers</h4>
              <div class="d-flex flex-wrap mb-2">
                <v-chip
                  v-for="d in role.dancers"
                  :key="d.dancerId"
                  small
                  class="mr-1 mb-1"
                >
                  {{ dancerName(d.dancerId) }}
                </v-chip>
                <span v-if="role.dancers.length === 0" class="caption grey--text">No dancers assigned.</span>
              </div>
              <v-btn small outlined @click="openAssignDialog(roleIdx)">Assign Dancers</v-btn>

              <v-divider class="my-4" />

              <!-- Costume items sub-section -->
              <h4>Costume Items</h4>
              <div
                v-for="(item, itemIdx) in role.costumeItems"
                :key="item.id"
                class="d-flex align-center mb-2"
              >
                <span class="flex-grow-1">
                  {{ item.description }}
                  <a
                    v-if="item.purchaseLink"
                    :href="item.purchaseLink"
                    target="_blank"
                    class="ml-2 caption"
                  >{{ item.purchaseLink.substring(0, 40) }}…</a>
                </span>
                <v-btn icon x-small color="red" @click="onRemoveCostumeItem(role, itemIdx)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>

              <v-expand-transition>
                <div v-if="showAddItemForRole === role.id" class="mt-2">
                  <v-text-field
                    v-model="newCostumeItem.description"
                    label="Description"
                    outlined
                    dense
                  />
                  <v-text-field
                    v-model="newCostumeItem.purchaseLink"
                    label="Purchase Link (URL)"
                    outlined
                    dense
                  />
                  <v-btn small color="primary" @click="onAddCostumeItem(role)">Add Item</v-btn>
                  <v-btn small text @click="showAddItemForRole = null">Cancel</v-btn>
                </div>
              </v-expand-transition>
              <v-btn
                v-if="showAddItemForRole !== role.id"
                small
                outlined
                class="mt-2"
                @click="onOpenAddCostumeItem(role.id)"
              >
                + Add Item
              </v-btn>

            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-btn outlined class="mt-3" @click="newRoleDialog = true">+ Add Role</v-btn>

      </v-flex>
    </v-layout>

    <!-- Add Role Dialog -->
    <v-dialog v-model="newRoleDialog" width="400">
      <v-card>
        <v-card-title>New Role</v-card-title>
        <v-card-text>
          <v-text-field v-model="newRoleName" label="Role Name" outlined dense autofocus />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="newRoleDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!newRoleName.trim()" @click="onAddRole">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Dancers Dialog -->
    <v-dialog v-model="assignDialog" width="700" max-height="80vh">
      <v-card>
        <v-card-title>Assign Dancers to Role</v-card-title>
        <v-card-text>

          <!-- Step 1: Paste names -->
          <div v-if="assignStep === 'paste'">
            <p>Paste dancer names (one per line, or tab-separated from Google Sheets):</p>
            <v-textarea
              v-model="pastedNames"
              outlined
              rows="8"
              placeholder="Jane Smith&#10;Mary Johnson&#10;..."
            />
            <v-btn color="primary" :disabled="!pastedNames.trim()" @click="onParseNames">
              Parse Names
            </v-btn>
          </div>

          <!-- Step 2: Review matches -->
          <div v-if="assignStep === 'review'">
            <p class="green--text"><strong>Matched ({{ matchedDancers.length }}):</strong></p>
            <div
              v-for="m in matchedDancers"
              :key="m.parsedName"
              class="d-flex align-center mb-1"
            >
              <v-icon small color="green" class="mr-2">mdi-check-circle</v-icon>
              <span>{{ m.parsedName }} → <strong>{{ m.firstName }} {{ m.lastName }}</strong></span>
            </div>

            <p v-if="unmatchedDancers.length > 0" class="red--text mt-3">
              <strong>Unmatched ({{ unmatchedDancers.length }}):</strong>
            </p>
            <div
              v-for="u in unmatchedDancers"
              :key="u.parsedName"
              class="d-flex align-center mb-2"
            >
              <v-icon small color="red" class="mr-2">mdi-close-circle</v-icon>
              <span class="mr-2" style="min-width:160px">{{ u.parsedName }}</span>
              <v-select
                v-model="u.resolvedDancerId"
                :items="allDancerSelectItems"
                label="Select dancer or skip"
                outlined
                dense
                hide-details
                style="max-width:260px"
              />
            </div>

            <div class="d-flex mt-3">
              <v-btn text @click="assignStep = 'paste'">Back</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" :loading="savingAssign" @click="onConfirmAssign">
                Confirm &amp; Save
              </v-btn>
            </div>
          </div>

        </v-card-text>
        <v-card-actions v-if="assignStep === 'paste'">
          <v-spacer></v-spacer>
          <v-btn text @click="assignDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<style scoped>
h2 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  letter-spacing: -.02em;
}
h3 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 400;
  font-size: 22px;
  margin-bottom: 8px;
}
h4 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 400;
  font-size: 18px;
  margin-bottom: 8px;
}
</style>

<script>
export default {
  props: ['id'],
  data () {
    return {
      form: {
        title: '',
        description: '',
        stageFormationUrl: '',
        roles: []
      },
      savingBasics: false,
      basicsSaved: false,
      savingRoleId: null,
      savedRoleId: null,
      deleteDialog: false,
      deleting: false,
      newRoleDialog: false,
      newRoleName: '',
      showAddVideoForRole: null,
      newVideo: { title: '', embedCode: '' },
      showAddItemForRole: null,
      newCostumeItem: { description: '', purchaseLink: '' },
      // dancer assignment
      assignDialog: false,
      assignRoleIdx: null,
      assignStep: 'paste',
      pastedNames: '',
      matchedDancers: [],
      unmatchedDancers: [],
      savingAssign: false
    }
  },
  computed: {
    piece () {
      return this.$store.getters.currentIABFCorpsPiece
    },
    allDancers () {
      return this.$store.getters.loadedIABFDancers
    },
    allDancerSelectItems () {
      const items = [{ text: '(Skip)', value: null }]
      this.allDancers.forEach(d => {
        items.push({ text: d.firstName + ' ' + d.lastName, value: d.id })
      })
      return items
    }
  },
  created () {
    this.$store.dispatch('loadIABFCorpsPiece', this.id).then(() => {
      if (this.piece) {
        this.form = JSON.parse(JSON.stringify(this.piece))
      }
    })
    this.$store.dispatch('loadIABFDancers')
  },
  methods: {
    async onSaveBasics () {
      this.savingBasics = true
      this.basicsSaved = false
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.savingBasics = false
      this.basicsSaved = true
      setTimeout(() => { this.basicsSaved = false }, 3000)
    },
    async onSaveRole (role) {
      this.savingRoleId = role.id
      this.savedRoleId = null
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.savingRoleId = null
      this.savedRoleId = role.id
      setTimeout(() => { this.savedRoleId = null }, 3000)
    },
    async onDelete () {
      this.deleting = true
      await this.$store.dispatch('deleteIABFCorpsPiece', this.id)
      this.$router.push('/iabf/admin/corps-pieces')
    },
    onAddRole () {
      const name = this.newRoleName.trim()
      if (!name) return
      this.form.roles.push({
        id: 'role_' + Date.now(),
        name,
        costumeColorField: '',
        costumeDescription: '',
        shareVideosFromRole: null,
        videos: [],
        dancers: [],
        costumeItems: []
      })
      this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.newRoleName = ''
      this.newRoleDialog = false
    },
    onRemoveRole (roleIdx) {
      if (!confirm('Remove this role?')) return
      this.form.roles.splice(roleIdx, 1)
      this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
    },
    shareVideoOptions (currentRoleId) {
      const items = [{ text: '(none)', value: null }]
      this.form.roles.forEach(r => {
        if (r.id !== currentRoleId) {
          items.push({ text: r.name || r.id, value: r.id })
        }
      })
      return items
    },
    roleNameById (roleId) {
      const role = this.form.roles.find(r => r.id === roleId)
      return role ? role.name : roleId
    },
    sortedVideos (videos) {
      return (videos || []).slice().sort((a, b) => a.order - b.order)
    },
    onOpenAddVideo (roleId) {
      this.showAddVideoForRole = roleId
      this.newVideo = { title: '', embedCode: '' }
    },
    async onAddVideo (role) {
      if (!this.newVideo.title.trim()) return
      const sorted = this.sortedVideos(role.videos)
      const maxOrder = sorted.length > 0 ? sorted[sorted.length - 1].order : 0
      role.videos.push({
        id: 'v_' + Date.now(),
        title: this.newVideo.title.trim(),
        embedCode: this.newVideo.embedCode.trim(),
        order: maxOrder + 1
      })
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.showAddVideoForRole = null
    },
    async onMoveVideoUp (role, vidIdx) {
      const sorted = this.sortedVideos(role.videos)
      if (vidIdx === 0) return
      const tmp = sorted[vidIdx].order
      sorted[vidIdx].order = sorted[vidIdx - 1].order
      sorted[vidIdx - 1].order = tmp
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
    },
    async onMoveVideoDown (role, vidIdx) {
      const sorted = this.sortedVideos(role.videos)
      if (vidIdx === sorted.length - 1) return
      const tmp = sorted[vidIdx].order
      sorted[vidIdx].order = sorted[vidIdx + 1].order
      sorted[vidIdx + 1].order = tmp
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
    },
    async onRemoveVideo (role, vidIdx) {
      const sorted = this.sortedVideos(role.videos)
      const videoId = sorted[vidIdx].id
      role.videos = role.videos.filter(v => v.id !== videoId)
      role.videos.forEach((v, i) => { v.order = i + 1 })
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
    },
    dancerName (dancerId) {
      const d = this.allDancers.find(x => x.id === dancerId)
      return d ? d.firstName + ' ' + d.lastName : dancerId
    },
    openAssignDialog (roleIdx) {
      this.assignRoleIdx = roleIdx
      this.assignStep = 'paste'
      this.pastedNames = ''
      this.matchedDancers = []
      this.unmatchedDancers = []
      this.assignDialog = true
    },
    onParseNames () {
      const raw = this.pastedNames
      const names = raw.split(/[\n\t,]/).map(n => n.trim()).filter(n => n.length > 0)
      const unique = [...new Set(names)]
      const dancers = this.allDancers

      this.matchedDancers = []
      this.unmatchedDancers = []

      unique.forEach(parsed => {
        const lower = parsed.toLowerCase()
        const match = dancers.find(d =>
          (d.firstName + ' ' + d.lastName).toLowerCase() === lower
        )
        if (match) {
          this.matchedDancers.push({ parsedName: parsed, ...match })
        } else {
          this.unmatchedDancers.push({ parsedName: parsed, resolvedDancerId: null })
        }
      })

      this.assignStep = 'review'
    },
    async onConfirmAssign () {
      this.savingAssign = true
      const role = this.form.roles[this.assignRoleIdx]

      const newIds = []
      this.matchedDancers.forEach(m => newIds.push(m.id))
      this.unmatchedDancers.forEach(u => {
        if (u.resolvedDancerId) newIds.push(u.resolvedDancerId)
      })

      const existingIds = new Set(role.dancers.map(d => d.dancerId))
      newIds.forEach(id => existingIds.add(id))
      role.dancers = [...existingIds].map(id => ({ dancerId: id }))

      // Recompute allDancerIds
      const allIds = new Set()
      this.form.roles.forEach(r => {
        r.dancers.forEach(d => allIds.add(d.dancerId))
      })
      this.form.allDancerIds = [...allIds]

      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.savingAssign = false
      this.assignDialog = false
    },
    onOpenAddCostumeItem (roleId) {
      this.showAddItemForRole = roleId
      this.newCostumeItem = { description: '', purchaseLink: '' }
    },
    async onAddCostumeItem (role) {
      if (!this.newCostumeItem.description.trim()) return
      role.costumeItems.push({
        id: 'ci_' + Date.now(),
        description: this.newCostumeItem.description.trim(),
        purchaseLink: this.newCostumeItem.purchaseLink.trim()
      })
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
      this.showAddItemForRole = null
    },
    async onRemoveCostumeItem (role, itemIdx) {
      role.costumeItems.splice(itemIdx, 1)
      await this.$store.dispatch('updateIABFCorpsPiece', { ...this.form, id: this.id })
    }
  }
}
</script>
